import { FadeIn, FadeInStagger } from "~/components/fade-in";
import { Svg } from "~/components/svg";
import type { Client } from "~/lib/client.server";

/**
 * The client roster as a bordered lattice rather than a logo strip — it is the
 * strongest proof we have, so it carries the top of the page.
 *
 * Laid out with wrapping flex rather than grid so `justify-center` applies per
 * line: full rows fill edge to edge, and a part-filled last row centres itself
 * instead of hanging off the left against dead space. Basis is a `calc`
 * division rather than `w-1/6`, whose rounded percentage can sum past 100% and
 * drop the sixth logo onto its own row.
 *
 * Each cell draws its own right and bottom rule and the list is nudged a pixel
 * past the frame, so the outermost rules clip. The very last cell drops its
 * right rule, which leaves a centred final row open on both sides rather than
 * closed on one.
 */
export function LogoWall({
  clients,
  className,
}: {
  clients: Array<Client>;
  className?: string;
}) {
  return (
    <FadeInStagger faster className={className}>
      <div className="overflow-hidden rounded-2xl border border-ink-700">
        <ul className="-mb-px -mr-px flex flex-wrap justify-center">
          {clients.map((client) => (
            <li
              key={client.name}
              className="shrink-0 grow-0 basis-[calc(100%/2)] border-b border-r border-ink-700 last:border-r-0 sm:basis-[calc(100%/3)] lg:basis-[calc(100%/6)]"
            >
              <FadeIn className="flex h-full min-h-[5.5rem] items-center justify-center px-5 py-7">
                {client.logo ? (
                  <Svg
                    name={client.logo.symbol}
                    aria-label={client.name}
                    className="max-w-full object-contain text-ink-100 opacity-75 transition duration-300 hover:opacity-100"
                    width={client.logo.width}
                    height={client.logo.height}
                  />
                ) : (
                  <span className="font-display text-base font-semibold tracking-tight text-ink-100 opacity-75 transition duration-300 hover:opacity-100">
                    {client.name}
                  </span>
                )}
              </FadeIn>
            </li>
          ))}
        </ul>
      </div>
    </FadeInStagger>
  );
}
